/**
 * SpecularAA — specular antialiasing by filtering ROUGHNESS before lighting
 * (shader additions wave, 2026-09-15; Kaplanyan et al. 2016 "Filtering
 * Distributions of Normals for Shading Antialiasing"; Toksvig 2006
 * "Mipmapping Normal Maps").
 *
 * WHY. A narrow specular lobe on a bumpy surface lands on a different
 * sub-pixel normal every frame; the highlight sparkles, and post AA only ever
 * sees the already-aliased result. Widening the lobe by the variance the pixel
 * footprint cannot resolve attacks the source.
 *
 * WHAT three ALREADY DOES. r185's physical material folds a GEOMETRIC term
 * into every roughness read: `getRoughness()` adds `getGeometryRoughness()`,
 * the max |dFdx / dFdy| of the interpolated geometry normal. Curvature across
 * the pixel is therefore already handled, and stacking a second derivative
 * filter on top double-counts it — so the geometric term here is OFF by
 * default (`geometricGain: 0`) and exists only for audits.
 *
 * WHAT IT LACKS. The NORMAL MAP: detail the mip chain has averaged away inside
 * the footprint. A mip-filtered normal-map texel that averaged competing
 * directions comes back SHORTER than unit length, and that shortening IS the
 * unresolved variance (Toksvig):
 *
 *   |n|  = length of the decoded texel      (1 at the base mip, shorter up the chain)
 *   s    = 2/α² − 2                         Blinn-Phong power for GGX α, α = r²
 *   ft   = |n| / (|n| + s·(1 − |n|))        the Toksvig factor
 *   α_f² = α² / ft                          filtered variance — never below α²
 *   r_f  = (α_f²)^¼                         back through the SAME map (r = √α)
 *
 * The filtering happens in the α² (variance) domain and returns through the
 * same r ↔ α map; adding variance to r directly is a different model.
 * `varianceCap` bounds α_f² − α² so one shortened texel cannot roughen a mirror
 * to chalk, and `strength` blends the result in.
 *
 * COMPOSES: patches `roughnessNode` OVER the material's existing roughnessNode
 * (or `materialRoughness`), so wetness / detail / accumulation layers keep
 * working. Apply it LAST among roughness modifiers.
 */
import type * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, texture, normalView, dFdx, dFdy, materialRoughness, mix, pow, saturate,
} from 'three/tsl';

export interface SpecularAAConfig {
  /** Use the material's normal-map texel length (Toksvig) when it has one (default true). */
  normalMap?: boolean;
  /** An explicit normal map to read the Toksvig length from (a material that blends several, or has none set). */
  normalMapTexture?: THREE.Texture | null;
  /** Extra geometric term gain — OFF by default: three already applies one. */
  geometricGain?: number;
  /** Cap on the variance ADDED in the α² domain (default 0.25). */
  varianceCap?: number;
  /** Blend of filtered vs authored roughness, 0..1 (default 1). */
  strength?: number;
}

export interface SpecularAAHandle {
  strength: { value: number };
  geometricGain: { value: number };
  varianceCap: { value: number };
  /** True when a normal map was found and the Toksvig term is live. */
  usesNormalMap: boolean;
}

/** Blinn-Phong specular power for a GGX α (r = perceptual roughness, α = r²). */
export function ggxSharpnessCpu(roughness: number): number {
  const a = Math.max(roughness * roughness, 0.02);
  return Math.max(1, 2 / (a * a) - 2);
}

/** Toksvig-filtered perceptual roughness for a decoded normal-map texel of length `len`. */
export function toksvigRoughnessCpu(roughness: number, len: number, varianceCap = 0.25): number {
  const r = Math.min(1, Math.max(0, roughness));
  const l = Math.min(1, Math.max(0.05, len));
  const alpha2 = r * r * r * r; // (r²)² — the variance domain
  const s = ggxSharpnessCpu(r);
  const ft = Math.max(1e-4, l / (l + s * (1 - l)));
  const filtered = Math.min(alpha2 / ft, alpha2 + varianceCap);
  return Math.max(r, Math.min(1, Math.pow(Math.min(1, filtered), 0.25)));
}

/** Filtered roughness from an explicit ADDED variance (the geometric / texture-moment form). */
export function filteredRoughnessCpu(roughness: number, variance: number, varianceCap = 0.25): number {
  const r = Math.min(1, Math.max(0, roughness));
  const alpha2 = r * r * r * r;
  const v = Math.min(Math.max(0, variance), varianceCap);
  return Math.max(r, Math.min(1, Math.pow(Math.min(1, alpha2 + v), 0.25)));
}

export function applySpecularAA(
  material: THREE.Material & { roughnessNode?: unknown; normalMap?: THREE.Texture | null; needsUpdate?: boolean },
  config?: SpecularAAConfig,
): SpecularAAHandle {
  const uStrength = uniform(config?.strength ?? 1);
  const uGain = uniform(config?.geometricGain ?? 0);
  const uCap = uniform(config?.varianceCap ?? 0.25);
  const map = config?.normalMap === false ? null : (config?.normalMapTexture ?? material.normalMap ?? null);
  const prev = material.roughnessNode;

  material.roughnessNode = Fn(() => {
    const r = float((prev ?? materialRoughness) as never).toVar();
    const alpha = r.mul(r);
    const alpha2 = alpha.mul(alpha).toVar();          // α² — the variance domain
    const a2f = alpha2.toVar();
    if (map) {
      // Toksvig: the mip-filtered texel's length measures the unresolved variance.
      const n = texture(map).xyz.mul(2).sub(1);
      const len = saturate(n.length()).max(0.05);
      const s = float(2).div(alpha2.max(1e-4)).sub(2).max(1);
      const ft = len.div(len.add(s.mul(float(1).sub(len)))).max(1e-4);
      a2f.assign(alpha2.div(ft).min(alpha2.add(uCap)));
    }
    // Optional EXTRA geometric term (squared shading-normal derivatives) —
    // off by default: three's getGeometryRoughness already covers geometry.
    const dx = dFdx(normalView), dy = dFdy(normalView);
    const vg = uGain.mul(dx.dot(dx).add(dy.dot(dy)));
    a2f.addAssign(vg.min(uCap));
    const rf = pow(a2f.min(1), 0.25).max(r);            // (α²)^¼ = √α = r
    return mix(r, rf, uStrength).min(1);
  })();
  material.needsUpdate = true;

  return {
    strength: uStrength as unknown as { value: number },
    geometricGain: uGain as unknown as { value: number },
    varianceCap: uCap as unknown as { value: number },
    usesNormalMap: !!map,
  };
}
